import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Mountain } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { settingsApi } from "@/services/settings";
import { apiConfig } from "@/services/api";
import { SECTION_LABELS, type NavItem, type NavSection, visibleNavItems } from "./navConfig";
import { useNavFlags } from "./useNavFlags";
import { useNavBadges } from "./useNavBadges";

const brandImg = (u: string) => (u.startsWith("http") ? u : `${apiConfig.base}${u}`);

// 折叠分组持久化（刷新后保留用户的折叠偏好）
const COLLAPSED_KEY = "ota-nav-collapsed-sections";
function loadCollapsed(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? "[]"));
  } catch {
    return new Set();
  }
}

interface SidebarProps {
  /** 移动端外控 · 默认桌面端常显 */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
  /** 桌面端整条侧栏收起（隐藏 → 右侧全屏）· 由 Layout 控制 */
  desktopCollapsed?: boolean;
}

export function Sidebar({ mobileOpen = false, onMobileClose, desktopCollapsed = false }: SidebarProps) {
  const permissions = useAuthStore((s) => s.user?.permissions);
  const navFlags = useNavFlags();
  const items = visibleNavItems(permissions, navFlags);
  // 品牌中心化读取（系统设置可改·logo 图 + 公司简称）
  const { data: branding } = useQuery({ queryKey: ["branding"], queryFn: settingsApi.getBranding, staleTime: 5 * 60_000 });
  const sections: NavSection[] = ["main", "resources", "business", "ops", "system"];

  // 分组折叠状态（持久化）
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(loadCollapsed);
  const toggleSection = (s: string) =>
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      localStorage.setItem(COLLAPSED_KEY, JSON.stringify([...next]));
      return next;
    });

  const badgeFor = useNavBadges(items);

  return (
    <>
      {/* mobile backdrop */}
      {mobileOpen && (
        <div
          aria-hidden
          onClick={onMobileClose}
          className="fixed inset-0 z-30 bg-slate-900/40 md:hidden"
        />
      )}
      <aside
        data-testid="sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex h-screen w-60 shrink-0 flex-col gap-1 overflow-y-auto bg-slate-900 p-5 text-slate-100 transition-transform md:sticky md:top-0 md:bottom-auto md:translate-x-0 md:self-start",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          // 桌面端整条收起 → 隐藏侧栏（右侧 flex 内容自动占满全屏）
          desktopCollapsed && "md:hidden",
        )}
      >
        <div className="flex items-center gap-2.5 p-2">
          {branding?.logo_url ? (
            <img src={brandImg(branding.logo_url)} alt="logo" className="h-8 w-8 rounded-lg object-cover" />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <Mountain className="h-[18px] w-[18px] text-white" strokeWidth={2} />
            </div>
          )}
          <span className="text-[17px] font-bold text-slate-50">{branding?.brand_name || "悠途"}</span>
        </div>
        <div className="h-5" />

        {sections.map((section) => {
          const list = items.filter((i) => i.section === section);
          if (list.length === 0) return null;
          const isCollapsed = collapsedSections.has(section);
          // 折叠时把组内徽标数汇总到组标题（不丢失"待派"等提醒）
          const sectionBadge = list.reduce((sum, it) => sum + (badgeFor(it) ?? 0), 0);
          return (
            <div key={section} className="flex flex-col gap-1">
              <SectionLabel
                collapsed={isCollapsed}
                badge={isCollapsed ? sectionBadge : 0}
                onClick={() => toggleSection(section)}
              >
                {SECTION_LABELS[section]}
              </SectionLabel>
              {!isCollapsed &&
                list.map((item) => (
                  <SidebarLink key={item.id} item={item} badge={badgeFor(item)} onNavigate={onMobileClose} />
                ))}
              <div className="h-3" />
            </div>
          );
        })}
      </aside>
    </>
  );
}

function SectionLabel({
  children,
  collapsed,
  badge,
  onClick,
}: {
  children: React.ReactNode;
  collapsed: boolean;
  badge: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={!collapsed}
      className="group flex items-center gap-1.5 rounded-md px-3 py-1 text-[11px] font-semibold tracking-[1.5px] text-slate-500 transition-colors hover:text-slate-300"
    >
      <ChevronDown
        className={cn("h-3.5 w-3.5 shrink-0 transition-transform", collapsed && "-rotate-90")}
        strokeWidth={2.5}
      />
      <span className="flex-1 text-left">{children}</span>
      {badge > 0 && (
        <span className="flex h-4 min-w-[18px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
          {badge}
        </span>
      )}
    </button>
  );
}

/** 官网收款的 stage → 入口分组：订单中心入口 = 待初筛；财务入口 = 待查账 / 异常 / 已完成。 */
const stageGroup = (s: string | null) => (s === "audit" || s === "exception" || s === "done" ? "finance" : "orders");

function SidebarLink({ item, badge, onNavigate }: { item: NavItem; badge?: number; onNavigate?: () => void }) {
  const Icon = item.icon;
  const location = useLocation();
  // active 判定：精确匹配 / 子路径前缀（除 "/" 根）；带 ?stage= 的子项（官网收款）按路径 + stage 判
  const [toPath, toQuery] = item.to.split("?");
  const isActive = item.sub
    ? location.pathname === toPath && stageGroup(new URLSearchParams(location.search).get("stage")) === stageGroup(new URLSearchParams(toQuery ?? "").get("stage"))
    : item.to === "/"
      ? location.pathname === "/"
      : location.pathname === item.to || location.pathname.startsWith(item.to + "/");
  return (
    <NavLink
      to={item.to}
      state={item.to === "/orders" ? { fromSidebar: true } : undefined}   // ORDTAB-1 · 只有点左侧菜单进订单中心才恢复上次记住的 Tab；其它入口（首页待办 / 铃铛 / 搜索…）一律落「全部」
      onClick={onNavigate}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
        item.sub && "ml-4 py-1.5 text-[13px]",   // 子项缩进（样稿 └ 官网收款）
        isActive
          ? "bg-blue-600 font-semibold text-white"
          : "font-medium text-slate-300 hover:bg-slate-800 hover:text-white",
      )}
    >
      <Icon
        className={cn("h-5 w-5 shrink-0", isActive ? "text-white" : "text-slate-400")}
        strokeWidth={2}
      />
      <span className="flex-1 text-left">{item.labelCn}</span>
      {badge ? (
        <span className="flex h-5 min-w-[24px] items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}
